import { type ProspectContactDto, type ProspectDto } from "@crowdcoursing/api/openapi";
import { Delete, Edit, MoreVert, PushPin, Work } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";

import { ContactForm } from "~ui/molecules/ContactForm/ContactForm";
import { EmailWithCopyIcon } from "~ui/molecules/EmailWithCopyIcon/EmailWithCopyIcon";
import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";

const tPrimary = "Primary";
const tAdded = "Added";
const tUpdated = "Updated";
const tEditContact = "Edit contact";
const tDeleteContact = "Delete contact";
const tContacts = "Contacts";
const tAddPrimaryContact = "Add Primary Contact";
const tAddContact = "Add Contact";
const tNoContacts = "No contacts yet. Add contacts to track people involved with this prospect.";
const tCancel = "Cancel";
const tSaveChanges = "Save Changes";
const tEditContactTitle = "Edit Contact";
const tDeleteContactTitle = "Delete Contact";
const tDeleteContactConfirmation = "Are you sure you want to delete this contact?";
const tContact = "Contact:";
const tPrimaryContact = "(Primary Contact)";
const tDeleteConfirmation = "This action cannot be undone.";
const tDelete = "Delete";

interface PeopleTabProps {
  prospect: ProspectDto;
  contactModal: {
    isAddOpen: boolean;
    editContact: ProspectContactDto | null;
    contactToDelete: ProspectContactDto | null;
    deleteConfirmOpen: boolean;
    openAddModal: () => void;
    closeAddModal: () => void;
    openEditModal: (contact: ProspectContactDto) => void;
    closeEditModal: () => void;
    openDeleteModal: (contact: ProspectContactDto) => void;
    closeDeleteModal: () => void;
    handleAddContact: (
      values: Omit<ProspectContactDto, "id" | "prospectId" | "createdAt" | "updatedAt">,
    ) => Promise<void>;
    handleUpdateContact: (
      values: Omit<ProspectContactDto, "id" | "prospectId" | "createdAt" | "updatedAt">,
    ) => Promise<void>;
    handleDeleteContact: () => Promise<void>;
  };
}

interface ContactCardProps {
  contact: ProspectContactDto;
  onEdit: (contact: ProspectContactDto) => void;
  onDelete: (contact: ProspectContactDto) => void;
}

const ContactCard = ({ contact, onEdit, onDelete }: ContactCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        position: "relative", // For absolute positioning of action button
      }}
    >
      {/* Action Menu - Positioned in top right corner */}
      <Box
        sx={{
          position: "absolute",
          right: "8px",
          top: "8px",
        }}
      >
        <IconButton
          size="small"
          onClick={handleMenuClick}
          aria-label="contact actions"
          aria-controls={open ? "contact-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          id="contact-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            horizontal: "right",
            vertical: "bottom",
          }}
          transformOrigin={{
            horizontal: "right",
            vertical: "top",
          }}
        >
          <MenuItem onClick={() => handleMenuAction(() => onEdit(contact))}>
            <Edit fontSize="small" sx={{ color: "primary.main", mr: 1 }} />
            {tEditContact}
          </MenuItem>
          <MenuItem onClick={() => handleMenuAction(() => onDelete(contact))}>
            <Delete fontSize="small" sx={{ color: "error.main", mr: 1 }} />
            {tDeleteContact}
          </MenuItem>
        </Menu>
      </Box>

      <Box sx={{ width: "100%" }}>
        {/* Name and Role */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 0.5, pr: 4 }}>
            {[contact.firstName, contact.lastName].filter(Boolean).join(" ")}
            {contact.isPrimary && <Chip label={tPrimary} color="primary" size="small" sx={{ ml: 1 }} />}
          </Typography>
          {contact.roleTitle && (
            <Typography variant="body2" color="text.secondary" sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
              <Work fontSize="small" />
              {contact.roleTitle}
            </Typography>
          )}
        </Box>

        {/* Contact Info */}
        <Stack spacing={1} sx={{ mt: 1 }}>
          {contact.email && <EmailWithCopyIcon email={contact.email} />}
          {contact.phone && <PhoneWithCopyIcon phoneNumber={contact.phone} />}
        </Stack>

        {/* Notes */}
        {contact.notes && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {contact.notes}
          </Typography>
        )}

        {/* Timestamp */}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, whiteSpace: "nowrap" }}>
          {tAdded} {DateTime.fromISO(contact.createdAt ?? "").toRelative()}
          {contact.updatedAt !== contact.createdAt &&
            ` • ${tUpdated} ${DateTime.fromISO(contact.updatedAt ?? "").toRelative()}`}
        </Typography>
      </Box>
    </Paper>
  );
};

export const PeopleTab = ({ prospect, contactModal }: PeopleTabProps) => {
  const primaryContact = prospect.contacts?.find((c) => c.isPrimary);

  // Sort contacts: primary first, then by name
  const sortedContacts = [...(prospect.contacts ?? [])].sort((a, b) => {
    // Primary contacts first
    if (a.isPrimary !== b.isPrimary) {
      return a.isPrimary ? -1 : 1;
    }
    // Then sort by name
    const aName = [a.firstName, a.lastName].filter(Boolean).join(" ");
    const bName = [b.firstName, b.lastName].filter(Boolean).join(" ");
    return aName.localeCompare(bName);
  });

  return (
    <Box display="grid" gap={3}>
      {/* Contacts */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{tContacts}</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={contactModal.openAddModal}
            startIcon={!primaryContact && <PushPin fontSize="small" />}
          >
            {!primaryContact ? tAddPrimaryContact : tAddContact}
          </Button>
        </Box>
        <Box
          display="grid"
          gap={2}
          sx={{
            gridTemplateColumns: {
              md: "repeat(3, 1fr)", // Three equal columns on medium screens and up
              xs: "1fr", // Single column on small screens
            },
          }}
        >
          {sortedContacts.length > 0 ? (
            sortedContacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onEdit={contactModal.openEditModal}
                onDelete={contactModal.openDeleteModal}
              />
            ))
          ) : (
            <Typography color="text.secondary">{tNoContacts}</Typography>
          )}
        </Box>
      </Paper>

      {/* Add Contact Dialog */}
      {/* Add Contact Dialog */}
      <Dialog open={contactModal.isAddOpen} onClose={contactModal.closeAddModal} maxWidth="sm" fullWidth>
        <DialogTitle>{tAddContact}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <ContactForm
              onSubmit={contactModal.handleAddContact}
              initialValues={{ isPrimary: !primaryContact }} // Auto-set primary if none exists
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={contactModal.closeAddModal}>{tCancel}</Button>
          <Button type="submit" form="contact-form" variant="contained">
            {tAddContact}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={Boolean(contactModal.editContact)} onClose={contactModal.closeEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>{tEditContactTitle}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <ContactForm
              initialValues={{
                email: contactModal.editContact?.email ?? null,
                firstName: contactModal.editContact?.firstName ?? "",
                isPrimary: contactModal.editContact?.isPrimary ?? false,
                lastName: contactModal.editContact?.lastName ?? "",
                notes: contactModal.editContact?.notes ?? null,
                phone: contactModal.editContact?.phone ?? null,
                roleTitle: contactModal.editContact?.roleTitle ?? null,
              }}
              onSubmit={contactModal.handleUpdateContact}
              disablePrimaryToggle={contactModal.editContact?.isPrimary}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={contactModal.closeEditModal}>{tCancel}</Button>
          <Button type="submit" form="contact-form" variant="contained">
            {tSaveChanges}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={contactModal.deleteConfirmOpen} onClose={contactModal.closeDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle>{tDeleteContactTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tDeleteContactConfirmation}{" "}
            {contactModal.contactToDelete && (
              <>
                <br />
                <br />
                {tContact}{" "}
                <strong>
                  {[contactModal.contactToDelete.firstName, contactModal.contactToDelete.lastName]
                    .filter(Boolean)
                    .join(" ")}
                </strong>
                {contactModal.contactToDelete.isPrimary && tPrimaryContact}
              </>
            )}
            <br />
            <br />
            {tDeleteConfirmation}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={contactModal.closeDeleteModal}>{tCancel}</Button>
          <Button onClick={contactModal.handleDeleteContact} color="error" variant="contained">
            {tDelete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
